/**
 * FairnessAudit — HR-tier audit surface.
 *
 * Per Handoff §7e + Design Brief §6f: this is the only place dashboard-style
 * data density is allowed (it serves a structured audit task), but even here
 * prefer prose-grounded findings ("Marcus pulled Sarah's Operating Manual 12
 * times in the 14 days before her PIP") over chart-grounded ones ("Manager
 * pull frequency anomaly: +5σ").
 *
 * Six V1 audit queries selectable from a side panel. Results render as
 * structured rows with prose-grounded summaries.
 */

import { useState, useEffect } from "react";
import { useSession } from "../lib/useSession.js";
import { supabase } from "../lib/supabase.js";
import { Callout, SectionHeader } from "../components/index.js";

const QUERIES = [
  { id: "q1_pre_pip_edits",                label: "Pre-PIP edit window",            description: "Edits to a Profile in the 14 days before a PIP was delivered." },
  { id: "q2_tag_trend",                    label: "Tag-trend anomaly",              description: "Profiles whose negative-tag rate has spiked sharply versus baseline." },
  { id: "q3_viewing_frequency",            label: "Viewing-frequency anomaly",      description: "Managers pulling one Profile far more than peers." },
  { id: "q4_tension_alignment_misuse",     label: "Tension Alignment misuse",       description: "Surfaces flagged as manipulation-pattern by the misuse classifier." },
  { id: "q5_scapegoating",                 label: "Scapegoating signature",         description: "Profiles repeatedly flagged as fault-line across multiple Team Blueprints." },
  { id: "q6_rapid_relational_regeneration",label: "Rapid Relational regeneration",  description: "3+ Relational Blueprints on same subject in 7 days by same generator." },
];

export default function FairnessAuditPage() {
  const { session, appUser, loading } = useSession();
  const [activeQuery, setActiveQuery] = useState(QUERIES[0].id);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const allowed = appUser?.is_hr_admin || appUser?.is_tenant_admin || appUser?.tier === "hr_director" || appUser?.tier === "enterprise";

  const run = async (query_id) => {
    setActiveQuery(query_id);
    setRunning(true);
    setError(null);
    try {
      const token = session?.access_token;
      const res = await fetch("/.netlify/functions/fairness-audit", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ query_id, params: {} }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Audit query failed");
        setResults(null);
      } else {
        setResults(body);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <Loading />;
  if (!session) return <SignInPrompt />;
  if (!allowed) return <NotAllowed />;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px" }}>
      <header style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-3xl)",
            color: "var(--ppr-color-accent-navy)",
            letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
            margin: "0 0 8px",
          }}
        >
          Consistency audit
        </h1>
        <p
          style={{
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-base)",
            color: "var(--ppr-color-text-secondary)",
            margin: 0,
          }}
        >
          Six structured queries that surface anomalies in how the product is being used. Findings are information for support, not a watchlist.
        </p>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 24 }}>
        <aside>
          <div
            style={{
              fontFamily: "var(--ppr-typography-font-family-display)",
              fontSize: "var(--ppr-typography-font-size-sm)",
              letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
              textTransform: "uppercase",
              color: "var(--ppr-color-text-secondary)",
              marginBottom: 12,
            }}
          >
            Queries
          </div>
          {QUERIES.map((q) => (
            <button
              key={q.id}
              onClick={() => run(q.id)}
              disabled={running}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                marginBottom: 6,
                padding: "12px 14px",
                border: activeQuery === q.id
                  ? "1px solid var(--ppr-color-border-default)"
                  : "1px solid var(--ppr-color-border-default)",
                background: activeQuery === q.id
                  ? "var(--ppr-color-accent-navy-soft)"
                  : "var(--ppr-color-background-surface)",
                cursor: running ? "wait" : "pointer",
                fontFamily: "var(--ppr-typography-font-family-ui)",
                fontSize: "var(--ppr-typography-font-size-sm)",
                color: "var(--ppr-color-text-primary)",
              }}
            >
              <div style={{ fontWeight: 600 }}>{q.label}</div>
              <div
                style={{
                  fontSize: "var(--ppr-typography-font-size-xs)",
                  color: "var(--ppr-color-text-secondary)",
                  marginTop: 4,
                }}
              >
                {q.description}
              </div>
            </button>
          ))}
        </aside>

        <main>
          <Callout.Remember>
            Findings are information for support — not a watchlist. Use them to ask the right next question of the data, not to draw conclusions about the people you're reading.
          </Callout.Remember>

          {running && <Loading text="Running query…" />}
          {error && <ErrorState error={error} />}
          {!running && !error && results && <Results results={results} />}
        </main>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function Loading({ text = "Loading…" }) {
  return (
    <p
      style={{
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        color: "var(--ppr-color-text-muted)",
      }}
    >
      {text}
    </p>
  );
}

function SignInPrompt() {
  return (
    <div style={{ maxWidth: 460, margin: "60px auto", padding: "0 24px" }}>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-3xl)",
          color: "var(--ppr-color-accent-navy)",
        }}
      >
        Sign in required
      </h1>
      <p>The consistency audit surface requires sign-in.</p>
    </div>
  );
}

function NotAllowed() {
  return (
    <div style={{ maxWidth: 460, margin: "60px auto", padding: "0 24px" }}>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-3xl)",
          color: "var(--ppr-color-accent-coral-dark)",
        }}
      >
        Pro tier required
      </h1>
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          color: "var(--ppr-color-text-secondary)",
        }}
      >
        The consistency audit surface is gated to Pro-tier accounts. Contact your admin to be granted access.
      </p>
    </div>
  );
}

function ErrorState({ error }) {
  return (
    <div
      style={{
        padding: "16px 18px",
        border: "2px solid var(--ppr-color-accent-coral)",
        background: "var(--ppr-color-accent-coral-very-soft)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-coral-dark)",
          marginBottom: 6,
        }}
      >
        Query failed
      </div>
      <div style={{ fontFamily: "var(--ppr-typography-font-family-body)" }}>{error}</div>
    </div>
  );
}

function Results({ results }) {
  return (
    <div>
      <SectionHeader index={results.result_count} title={results.label} />
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "var(--ppr-typography-font-size-xs)",
          color: "var(--ppr-color-text-muted)",
          marginBottom: 20,
        }}
      >
        {results.result_count} {results.result_count === 1 ? "match" : "matches"} — queried {new Date(results.queried_at).toLocaleString()}
      </p>

      {results.result_count === 0 ? (
        <Callout.Tip>
          No matches in the audit window. That's the expected outcome most of the time — these queries surface anomalies, not noise.
        </Callout.Tip>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {results.results.map((row, i) => (
            <ResultRow key={i} row={row} queryId={results.query_id} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResultRow({ row, queryId }) {
  const summary = formatPoseSummary(row, queryId);
  return (
    <article
      style={{
        padding: "16px 18px",
        border: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-background-surface)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: "var(--ppr-color-text-primary)",
          marginBottom: 8,
        }}
      >
        {summary}
      </div>
      <details>
        <summary
          style={{
            fontFamily: "var(--ppr-typography-font-family-mono)",
            fontSize: "var(--ppr-typography-font-size-xs)",
            color: "var(--ppr-color-text-muted)",
            cursor: "pointer",
          }}
        >
          Raw fields
        </summary>
        <pre
          style={{
            fontFamily: "var(--ppr-typography-font-family-mono)",
            fontSize: "var(--ppr-typography-font-size-xs)",
            color: "var(--ppr-color-text-secondary)",
            background: "var(--ppr-color-background-sub)",
            padding: "10px 12px",
            marginTop: 8,
            overflow: "auto",
          }}
        >
          {JSON.stringify(row, null, 2)}
        </pre>
      </details>
    </article>
  );
}

/**
 * Format an audit row as a prose-grounded sentence per Design Brief §10.9
 * recommendation. The prose form is what HR sees; the raw fields hide in
 * the expandable Details section.
 */
function formatPoseSummary(row, queryId) {
  switch (queryId) {
    case "q1_pre_pip_edits":
      return `${row.profile_first_name} had ${row.edit_count} Playbook edits in the 14 days before a PIP was delivered (PIP at ${fmtDate(row.pip_event_at)}).`;
    case "q2_tag_trend":
      return `${row.profile_first_name} accumulated ${row.recent_negative_count} negative tags in the recent window vs. ${row.baseline_negative_count} in the baseline — a ${row.spike_ratio}× spike.`;
    case "q3_viewing_frequency":
      return `A viewer pulled ${row.profile_first_name}'s Playbook ${row.pull_count} times in the recent window (most recent: ${fmtDate(row.most_recent_pull_at)}).`;
    case "q4_tension_alignment_misuse":
      return `User flagged ${row.flag_count} times for Tension Alignment misuse in the audit window, affecting ${row.affected_profile_count} ${row.affected_profile_count === 1 ? "Profile" : "Profiles"}. Most recent: ${fmtDate(row.most_recent_flag_at)}.`;
    case "q5_scapegoating":
      return `${row.profile_first_name} was tagged as a fault-line / escalation risk ${row.fault_line_assignment_count} times across ${row.distinct_blueprint_count} Team Blueprints by ${row.distinct_generator_count} ${row.distinct_generator_count === 1 ? "generator" : "generators"}.`;
    case "q6_rapid_relational_regeneration":
      return `A user generated ${row.blueprint_count} Relational Blueprints on ${row.subject_profile_first_name} in a 7-day window (first: ${fmtDate(row.first_generated_at)}, latest: ${fmtDate(row.most_recent_generated_at)}).`;
    default:
      return JSON.stringify(row);
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}
